<script setup lang="ts">
import Dropdown from 'primevue/dropdown';
import { useField } from 'vee-validate';
import {
  computed,
  inject,
  nextTick,
  onBeforeMount,
  onMounted,
  reactive,
  shallowRef,
  watch,
} from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';

import getUserLocation from './helpers/getUserLocation.helper';
import type {
  InputPhoneNumberEmits,
  InputPhoneNumberProps,
} from './InputPhoneNumber.vue.d';
import FieldWrapper from '../fieldwrapper/FieldWrapper.vue';
import { FieldValidation } from '../form/Form.vue.d';
import Icon from '../icon/Icon.vue';
import InputText from '../inputtext/InputText.vue';
import { Nullable } from '../ts-helpers.d';
import ValidatorMessage from '../validatormessage/ValidatorMessage.vue';

type DialCode = {
  code: string;
  dial_code: string;
  emoji: string;
  name: string;
};

const props = withDefaults(defineProps<InputPhoneNumberProps>(), {
  maxDigit: 14,
});

const emit = defineEmits<InputPhoneNumberEmits>();

onBeforeMount(async () => {
  dialCodes.value = await getDialCodes();
  if (!props.value) selectedDialCode.value = getUserDialCode();
});

onMounted(async () => {
  field.value = props.modelValue;
  if (props.useValidator) {
    Object.assign(
      field,
      useField(
        props.fieldName ?? 'numberInput',
        async (value: Nullable<number>) => {
          return await nextTick(() => setValidatorMessage(value));
        },
      ),
    );
  }

  if (props.value != null) setValue(props.value);

  if (props.modelValue != null) setValue(props.modelValue);
});

const { locale } = useWangsVueConfig('InputPhoneNumber');
const Preset = inject<Record<string, any>>('preset', {}).inputphonenumber;

const isShowOverlay = shallowRef<boolean>(false);

const invalidInput = computed(() => props.invalid || !!field.errorMessage);
const isChromeBased = navigator.userAgent.includes('Chrome');

const setValidatorMessage = (value: Nullable<number>): string | boolean => {
  if (props.invalid && typeof props.validatorMessage === 'string') {
    return props.validatorMessage;
  } else if (typeof props.validatorMessage !== 'string') {
    const { empty } = props.validatorMessage ?? {};

    if (value == null && props.mandatory) {
      return empty ?? locale.emptyInvalidText?.replace('{label}', props.label);
    }
  }

  return true;
};

const dialCodes = shallowRef<DialCode[]>();
const selectedDialCode = shallowRef<string>();
const phoneNumber = shallowRef<string>();

const phoneKey = shallowRef<number>(0);

const field = reactive<FieldValidation<Nullable<string>>>({ value: null });

const getDialCodes = async (): Promise<DialCode[]> => {
  const { default: data } = await import('lib/assets/json/dial-codes.json');
  return (data.filter((d) => d.dial_code) ?? []) as DialCode[]; // Some dial_code(s) is missing
};

const updateValue = (): void => {
  field.value = phoneNumber.value
    ? `${selectedDialCode.value} ${phoneNumber.value}`
    : undefined;
  emit('update:modelValue', field.value);
};
/**
 * Focusing input number after user select dial code
 */
const focusInput = (): void => {
  nextTick(() => {
    const input = document.getElementById(
      'phone-number-input',
    ) as HTMLInputElement | null;

    if (input) input.focus();
  });
};

const setInputModel = (dial: string, phone: string): void => {
  selectedDialCode.value = dial;
  phoneNumber.value = phone;
};

const parsePhoneValue = (
  model?: string,
): { dialCode: string; phoneNumber: string } | null => {
  const values = model?.split(' ');
  if (values?.length) {
    const phoneInput = values.pop();
    const dialCode = values.join(' ');
    return { dialCode, phoneNumber: phoneInput };
  }
  return null;
};

const setValue = (model?: string): void => {
  const parsed = parsePhoneValue(model);
  if (parsed) {
    setInputModel(parsed.dialCode, parsed.phoneNumber);
    field.value = phoneNumber.value
      ? `${selectedDialCode.value} ${phoneNumber.value}`
      : undefined;
  } else {
    field.value = undefined;
  }
};

/**
 * Get Dial Code by current user location if no initial value for dial code.
 */
const getUserDialCode = (): string => {
  if (selectedDialCode.value && field.value?.includes(selectedDialCode.value))
    // When setField or setFields executed before this component mount
    return selectedDialCode.value;

  if (dialCodes.value) {
    const { country } = getUserLocation();
    const foundDial = dialCodes.value.find((dial) => dial.name === country);

    if (foundDial) return foundDial.dial_code;
    return '+62';
  }

  return '+62';
};

/**
 * We need to watch the props.value changes for async data only once after it rendered.
 */
watch(
  () => props.value,
  (value) => {
    setValue(value);
  },
  { once: true },
);

watch(
  () => field.value,
  () => {
    const parsed = parsePhoneValue(field.value);
    if (parsed) setInputModel(parsed.dialCode, parsed.phoneNumber);
  },
);

watch(
  () => props.modelValue,
  (model) => {
    setValue(model);
  },
);
</script>

<template>
  <FieldWrapper
    :info="props.fieldInfo"
    :label="props.label"
    :mandatory="props.mandatory"
  >
    <div
      class="grid grid-cols-[max-content,auto] w-full"
      data-wv-section="phone-number-input-wrapper"
    >
      <Dropdown
        v-model="selectedDialCode"
        :class="Preset?.dialcode.root({ isChromeBased, invalidInput }).class"
        :disabled="props.disabled"
        :filter-fields="['dial_code', 'name']"
        :options="dialCodes"
        @change="updateValue"
        @hide="
          () => {
            focusInput();
            isShowOverlay = false;
          }
        "
        @show="isShowOverlay = true"
        data-wv-section="dialcode"
        filter
        filter-placeholder="Search"
        option-label="dial_code"
        option-value="dial_code"
      >
        <template #dropdownicon>
          <Icon
            v-bind="Preset.dialcode.dropdownicon({ state: { isShowOverlay } })"
          />
        </template>

        <template #option="{ option }">
          <div class="flex gap-2">
            <img :src="option.image" alt="country-img" style="height: 16px" />

            <span>{{ option.name }}</span>

            <span>{{ option.dial_code }}</span>
          </div>
        </template>
      </Dropdown>

      <InputText
        :key="phoneKey"
        v-model="phoneNumber"
        :allowed-characters="/\d/"
        :disabled="disabled"
        :input-class="Preset?.input({ isChromeBased, invalidInput }).class"
        :max-length="maxDigit"
        :placeholder="placeholder ?? locale.defaultPlaceholder"
        :use-field-wrapper="false"
        :use-input-group="false"
        :use-validator="false"
        @blur="updateValue"
        data-wv-section="phoneinput"
        input-id="phone-number-input"
        prevent-input-on-max-length
      />
    </div>

    <ValidatorMessage
      v-show="field.errorMessage"
      :message="field.errorMessage"
    />
  </FieldWrapper>
</template>
